// ─── International phone helpers (dependency-free, all countries) ───────────
// Donor forms collect a phone and store it canonically as E.164 for EVERY
// country — `+<dial><national>`, trunk 0 dropped (`+972501234567`,
// `+15551234567`). Keeping the country prefix means donations from abroad can
// still be reached by SMS/WhatsApp/email later. The Bit/GamaPay path converts
// `+972…` back to the local `05…` it requires (backend normalizeIsraeliMobile).
// We avoid libphonenumber-js to keep the donor page light: the only hardcoded
// data is the standard ISO-2 → calling-code table; localized country NAMES come
// from the platform's Intl.DisplayNames (Hebrew + English, no hand-typed list
// to drift), and flag emoji are derived from the ISO code. Israeli numbers are
// validated strictly (Bit SMS bounce on a malformed number); other countries
// get a generic E.164 length check.

import { isValidPhoneNumber, validatePhoneNumberLength } from 'libphonenumber-js';

export interface Country {
  code: string;   // ISO-2
  name: string;   // localized (Hebrew) display name
  nameEn: string;
  flag: string;   // emoji flag
  dial: string;   // country calling code, no '+'
}

// ISO-2 : calling-code. Covers all UN members + major territories.
const DIAL_TABLE = 'AF:93,AL:355,DZ:213,AD:376,AO:244,AG:1268,AR:54,AM:374,AU:61,AT:43,AZ:994,BS:1242,BH:973,BD:880,BB:1246,BY:375,BE:32,BZ:501,BJ:229,BT:975,BO:591,BA:387,BW:267,BR:55,BN:673,BG:359,BF:226,BI:257,KH:855,CM:237,CA:1,CV:238,CF:236,TD:235,CL:56,CN:86,CO:57,KM:269,CG:242,CD:243,CR:506,HR:385,CU:53,CY:357,CZ:420,DK:45,DJ:253,DM:1767,DO:1809,EC:593,EG:20,SV:503,GQ:240,ER:291,EE:372,SZ:268,ET:251,FJ:679,FI:358,FR:33,GA:241,GM:220,GE:995,DE:49,GH:233,GR:30,GD:1473,GT:502,GN:224,GW:245,GY:592,HT:509,HN:504,HK:852,HU:36,IS:354,IN:91,ID:62,IR:98,IQ:964,IE:353,IL:972,IT:39,CI:225,JM:1876,JP:81,JO:962,KZ:7,KE:254,KI:686,KW:965,KG:996,LA:856,LV:371,LB:961,LS:266,LR:231,LY:218,LI:423,LT:370,LU:352,MO:853,MG:261,MW:265,MY:60,MV:960,ML:223,MT:356,MH:692,MR:222,MU:230,MX:52,FM:691,MD:373,MC:377,MN:976,ME:382,MA:212,MZ:258,MM:95,NA:264,NR:674,NP:977,NL:31,NZ:64,NI:505,NE:227,NG:234,KP:850,MK:389,NO:47,OM:968,PK:92,PW:680,PS:970,PA:507,PG:675,PY:595,PE:51,PH:63,PL:48,PT:351,QA:974,RO:40,RU:7,RW:250,KN:1869,LC:1758,VC:1784,WS:685,SM:378,ST:239,SA:966,SN:221,RS:381,SC:248,SL:232,SG:65,SK:421,SI:386,SB:677,SO:252,ZA:27,KR:82,SS:211,ES:34,LK:94,SD:249,SR:597,SE:46,CH:41,SY:963,TW:886,TJ:992,TZ:255,TH:66,TL:670,TG:228,TO:676,TT:1868,TN:216,TR:90,TM:993,TV:688,UG:256,UA:380,AE:971,GB:44,US:1,UY:598,UZ:998,VU:678,VA:379,VE:58,VN:84,YE:967,ZM:260,ZW:263';

// Emoji flag from an ISO-2 code (regional-indicator letters).
function flagOf(code: string): string {
  return code.toUpperCase().replace(/./g, c => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

// Localized names via the platform — Hebrew + English with no hardcoded list.
let heNames: Intl.DisplayNames | undefined;
let enNames: Intl.DisplayNames | undefined;
try { heNames = new Intl.DisplayNames(['he'], { type: 'region' }); } catch { /* unsupported */ }
try { enNames = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { /* unsupported */ }

// Per-country digit-count + pattern validation lives in validateNational
// (libphonenumber-js metadata); this just builds the picker list (flag + dial +
// localized name) for every country, Israel pinned first.
function buildCountries(): Country[] {
  const list: Country[] = DIAL_TABLE.split(',').map(pair => {
    const [code, dial] = pair.split(':');
    return {
      code,
      dial,
      flag: flagOf(code),
      name: (heNames && heNames.of(code)) || code,
      nameEn: (enNames && enNames.of(code)) || code,
    };
  });
  // Israel pinned first (the default + most common donor), then alphabetical by
  // the localized Hebrew name so the long list is easy to scan.
  const il = list.find(c => c.code === 'IL')!;
  const rest = list.filter(c => c.code !== 'IL').sort((a, b) => a.name.localeCompare(b.name, 'he'));
  return [il, ...rest];
}

export const COUNTRIES: Country[] = buildCountries();

export const DEFAULT_COUNTRY: Country = COUNTRIES.find(c => c.code === 'IL') || COUNTRIES[0];

export function countryByCode(code: string | undefined | null): Country | undefined {
  if (!code) return undefined;
  return COUNTRIES.find(c => c.code === code.toUpperCase());
}

// Strip a leading trunk '0' (Israel, UK, etc. write the national number with a
// leading 0 locally but drop it after the country code).
function dropTrunkZero(digits: string): string {
  return digits.replace(/^0+/, '');
}

// Validate the national portion (digits typed by the user, may include a
// leading trunk 0) for the given country — by DIGIT COUNT and by country,
// using libphonenumber-js metadata. validatePhoneNumberLength catches
// TOO_SHORT / TOO_LONG / INVALID_LENGTH per country; isValidPhoneNumber adds
// the country's number-pattern check (e.g. a valid IL prefix). Falls back to a
// permissive E.164 range only if the metadata can't parse the input.
export function validateNational(country: Country, raw: string): boolean {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return false;
  const national = dropTrunkZero(digits);
  const e164 = '+' + country.dial + national;
  try {
    if (validatePhoneNumberLength(e164)) return false; // wrong digit count for the country
    return isValidPhoneNumber(e164);
  } catch {
    return national.length >= 4 && national.length <= 15;
  }
}

// The exact length problem for the given input, or null when the length is OK
// for the country — lets the UI tell the donor "too short / too long".
export function phoneLengthIssue(country: Country, raw: string): 'short' | 'long' | 'invalid' | null {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return null;
  const e164 = '+' + country.dial + dropTrunkZero(digits);
  try {
    const r = validatePhoneNumberLength(e164);
    if (r === 'TOO_SHORT') return 'short';
    if (r === 'TOO_LONG') return 'long';
    if (r) return 'invalid';
    return null;
  } catch {
    return null;
  }
}

// Israel-mobile check — Bit/GamaPay sends an SMS, so the number MUST be a
// mobile (05X). Used to warn the donor inline when a phone field doubles as the
// Bit contact number.
export function isIsraeliMobile(raw: string): boolean {
  const d = (raw || '').replace(/\D/g, '');
  const local = d.startsWith('972') ? '0' + d.slice(3) : d.startsWith('0') ? d : (d.length === 9 ? '0' + d : d);
  return /^05\d{8}$/.test(local);
}

// Produce the canonical stored value: E.164 for EVERY country, i.e.
// `+<dial><national>` with the trunk 0 dropped — `+972501234567`,
// `+15551234567`. Storing the country code (and no leading 0) means a donation
// from abroad keeps the prefix needed to reach the donor by SMS/WhatsApp/email
// later. The Bit/GamaPay path converts `+972…` back to the local `05…` it
// requires (backend normalizeIsraeliMobile), and the messaging/dedup helpers
// already accept E.164 — so nothing downstream breaks.
export function normalizePhone(country: Country, raw: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  if (!digits) return '';
  const national = dropTrunkZero(digits);
  return '+' + country.dial + national;
}

// Best-effort: split a previously-stored value back into {country, national}
// so editing a prefilled donor phone shows the right flag + digits.
export function parsePhone(value: string | undefined | null): { country: Country; national: string } {
  const v = (value || '').trim();
  if (!v) return { country: DEFAULT_COUNTRY, national: '' };
  const digits = v.replace(/\D/g, '');
  if (v.startsWith('+') || digits.startsWith('00')) {
    const dd = digits.replace(/^00/, '');
    // longest dial-code match wins (e.g. '1268' before '1', '972' before '9').
    const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
    for (const c of sorted) {
      if (dd.startsWith(c.dial)) {
        return { country: c, national: dd.slice(c.dial.length) };
      }
    }
  }
  // Israeli local: leading 0 + 9 digits, or 972 prefix.
  if (digits.startsWith('972')) return { country: DEFAULT_COUNTRY, national: digits.slice(3) };
  if (digits.startsWith('0')) return { country: DEFAULT_COUNTRY, national: digits.replace(/^0+/, '') };
  return { country: DEFAULT_COUNTRY, national: digits };
}
